import { useState } from 'react';
import { Award, Plus, Loader2 } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { usePortalCertifications, useUploadCertification } from '@/api/hooks/usePortal';
import type { PortalCertification } from '@/api/types/assessment.types';
import { CERT_TYPES } from '@grc/shared';
import { WithdrawCertificationModal } from './WithdrawCertificationModal';

function CertCard({
  cert,
  onWithdraw,
}: {
  cert: PortalCertification;
  onWithdraw?: (cert: PortalCertification) => void;
}) {
  const daysRemaining =
    cert.lifecycle_status === 'active' && cert.expiry_date
      ? differenceInDays(parseISO(cert.expiry_date), new Date())
      : null;

  const expiryTag =
    daysRemaining === null ? null
    : daysRemaining <= 0
    ? <StatusBadge status="expired" label="Expired" />
    : daysRemaining <= 30
    ? <StatusBadge status="expiring_soon" label={`Expiring in ${daysRemaining} days`} />
    : null;

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-accent-cyan shrink-0" />
            <div>
              <p className="font-medium text-text-primary">{cert.cert_type}</p>
              {cert.issuing_body && <p className="text-xs text-text-secondary">{cert.issuing_body}</p>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={cert.lifecycle_status} />
            {expiryTag}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs text-text-secondary">
          {cert.cert_date && (
            <div>
              <p className="font-medium text-text-primary/70">Issued</p>
              <p>{format(parseISO(cert.cert_date), 'MMM d, yyyy')}</p>
            </div>
          )}
          {cert.expiry_date && (
            <div>
              <p className="font-medium text-text-primary/70">Expires</p>
              <p>{format(parseISO(cert.expiry_date), 'MMM d, yyyy')}</p>
            </div>
          )}
        </div>

        {cert.scope_description && (
          <p className="text-xs text-text-secondary line-clamp-2">{cert.scope_description}</p>
        )}

        {onWithdraw && cert.lifecycle_status === 'active' && (
          <div className="pt-2 border-t border-border-subtle">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-accent-rose hover:text-accent-rose hover:bg-accent-rose/10"
              onClick={() => onWithdraw(cert)}
            >
              Withdraw
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CertCreateForm({ onClose }: { onClose: () => void }) {
  const createMutation = useUploadCertification();
  const [certType, setCertType] = useState<string>(CERT_TYPES[0]);
  const [issuingBody, setIssuingBody] = useState('');
  const [certDate, setCertDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [coverageStart, setCoverageStart] = useState('');
  const [coverageEnd, setCoverageEnd] = useState('');
  const [scope, setScope] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (coverageStart && coverageEnd && coverageStart > coverageEnd) {
      toast.error('Coverage start must be on or before coverage end');
      return;
    }
    createMutation.mutate(
      {
        cert_type: certType,
        issuing_body: issuingBody,
        cert_date: certDate,
        expiry_date: expiryDate,
        scope_description: scope || undefined,
        coverage_start: coverageStart || undefined,
        coverage_end: coverageEnd || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Certification added — pending verification');
          onClose();
        },
        onError: () => toast.error('Could not add certification'),
      },
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Add Certification</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Certification Type</Label>
            <select
              value={certType}
              onChange={(e) => setCertType(e.target.value)}
              className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none"
            >
              {CERT_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="issuing-body">Issuing Body</Label>
            <Input id="issuing-body" placeholder="e.g. AICPA, BSI" value={issuingBody} onChange={(e) => setIssuingBody(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cert-date">Certification Date</Label>
              <Input id="cert-date" type="date" value={certDate} onChange={(e) => setCertDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="expiry-date">Expiry Date</Label>
              <Input id="expiry-date" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} />
            </div>
          </div>
          {/* Coverage window (V987) — optional; the tenant surfaces "Not captured" when empty. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="coverage-start">Coverage Start</Label>
              <Input id="coverage-start" type="date" value={coverageStart} onChange={(e) => setCoverageStart(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="coverage-end">Coverage End</Label>
              <Input id="coverage-end" type="date" value={coverageEnd} onChange={(e) => setCoverageEnd(e.target.value)} min={coverageStart || undefined} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="scope">Scope Description</Label>
            <textarea
              id="scope"
              placeholder="Brief description of what is covered…"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              rows={2}
              className="w-full resize-none rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none"
            />
          </div>
          {/* Out-of-band: VeriGRC records metadata only — the document is exchanged elsewhere. */}
          <div className="rounded-md border border-border-subtle bg-bg-surface p-3">
            <p className="text-xs text-text-secondary">
              VeriGRC records certification details only — it does not store or transfer the certificate document. Share the document itself with the requesting organization via your approved secure channel, outside VeriGRC.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding…</>
              ) : 'Add Certification'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function PortalCertificationsPage() {
  const { data: certs, isLoading } = usePortalCertifications();
  const [showUpload, setShowUpload] = useState(false);
  const [withdrawingCert, setWithdrawingCert] = useState<PortalCertification | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary">Certifications</h1>
          <p className="mt-1 text-sm text-text-secondary">Add and manage your compliance certifications</p>
        </div>
        {!showUpload && (
          <Button size="sm" onClick={() => setShowUpload(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add Certification
          </Button>
        )}
      </div>

      {showUpload && <CertCreateForm onClose={() => setShowUpload(false)} />}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 w-full rounded-xl" />)}
        </div>
      ) : !certs || certs.length === 0 ? (
        <Card>
          <CardContent className="py-16 flex flex-col items-center gap-4">
            <Award className="h-12 w-12 text-text-secondary/40" />
            <div className="text-center">
              <p className="font-medium text-text-primary">No certifications yet</p>
              <p className="text-sm text-text-secondary mt-1">Add your compliance certifications to help assessors review your security posture</p>
            </div>
            <Button size="sm" onClick={() => setShowUpload(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              Add your first certification
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="muted" className="font-mono">{certs.length} total</Badge>
            <Badge variant="emerald">{certs.filter((c) => c.lifecycle_status === 'active').length} active</Badge>
            <Badge variant="amber">{certs.filter((c) => c.lifecycle_status === 'withdrawn').length} withdrawn</Badge>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {certs.map((c) => <CertCard key={c.id} cert={c} onWithdraw={setWithdrawingCert} />)}
          </div>
        </>
      )}

      <WithdrawCertificationModal
        cert={withdrawingCert}
        onClose={() => setWithdrawingCert(null)}
      />
    </div>
  );
}
